import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Trash2, Images } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const http = axios.create({ withCredentials: true });

export default function GalleryManager() {
  const [items, setItems] = useState([]);
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef(null);

  const load = () =>
    http.get(`${API}/gallery`)
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Erro ao carregar galeria."))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!file) {
      toast.error("Escolha uma imagem primeiro.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", caption);
    setUploading(true);
    try {
      await http.post(`${API}/admin/gallery`, fd);
      toast.success("Foto publicada na galeria do site.");
      setFile(null);
      setCaption("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro no upload.");
    } finally {
      setUploading(false);
    }
  };

  const del = async (id) => {
    try {
      await http.delete(`${API}/admin/gallery/${id}`);
      toast.success("Foto removida.");
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch {
      toast.error("Erro ao remover.");
    }
  };

  return (
    <main className="max-w-6xl mx-auto px-5 md:px-8 py-10" data-testid="gallery-manager">
      <h2 className="font-bebas text-4xl tracking-wide text-cream">Galeria de cortes</h2>
      <p className="text-sm text-smoke mt-1">As fotos aparecem na página inicial, na seção Galeria. Use jpg, png ou webp (máx. 8MB).</p>

      <div className="mt-8 border border-white/10 bg-surface p-5 flex flex-wrap items-end gap-3" data-testid="gallery-upload-form">
        <div className="flex-1 min-w-[220px]">
          <label htmlFor="gallery-file" className="text-xs uppercase tracking-[0.25em] text-smoke block mb-2">Foto</label>
          <input
            id="gallery-file"
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="gallery-file-input"
            className="focus-gold w-full bg-ink border border-white/15 text-cream font-manrope px-4 py-2.5 text-sm outline-none file:mr-4 file:bg-gold file:text-ink file:border-0 file:font-bebas file:tracking-widest file:px-4 file:py-1.5 file:cursor-pointer"
          />
        </div>
        <div className="flex-1 min-w-[220px]">
          <label htmlFor="gallery-caption" className="text-xs uppercase tracking-[0.25em] text-smoke block mb-2">Legenda (opcional)</label>
          <input
            id="gallery-caption"
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Ex.: Degradê navalhado"
            maxLength={120}
            data-testid="gallery-caption-input"
            className="focus-gold w-full bg-ink border border-white/15 text-cream font-manrope px-4 py-2.5 text-sm outline-none placeholder:text-smoke/50"
          />
        </div>
        <button
          onClick={upload}
          disabled={uploading || !file}
          data-testid="gallery-upload-button"
          className="focus-gold inline-flex items-center gap-2 bg-gold text-ink font-bebas text-lg tracking-widest px-6 py-2.5 hover:bg-golddark transition-colors disabled:opacity-40"
        >
          <Upload className="w-4 h-4" /> {uploading ? "Enviando..." : "Publicar"}
        </button>
      </div>

      {loading ? (
        <p className="py-16 text-center text-sm text-smoke" data-testid="gallery-loading">Carregando...</p>
      ) : items.length === 0 ? (
        <div className="py-16 text-center" data-testid="gallery-empty">
          <Images className="w-8 h-8 text-gold/40 mx-auto mb-4" />
          <p className="text-sm text-smoke">Nenhuma foto ainda. Envie a primeira!</p>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="gallery-grid">
          {items.map((it) => (
            <figure key={it.id} className="group relative aspect-[3/4] overflow-hidden border border-white/10" data-testid={`gallery-item-${it.id}`}>
              <img src={`${BACKEND}${it.url}`} alt={it.caption || "Foto da galeria"} className="w-full h-full object-cover" loading="lazy" />
              {it.caption && (
                <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-ink/90 to-transparent p-3 text-xs text-cream">
                  {it.caption}
                </figcaption>
              )}
              <button
                onClick={() => del(it.id)}
                aria-label="Remover foto"
                data-testid={`gallery-delete-${it.id}`}
                className="focus-gold absolute top-2 right-2 p-2 bg-ink/80 border border-white/15 text-smoke opacity-0 group-hover:opacity-100 hover:border-red-500 hover:text-red-400 transition-[opacity,border-color,color] duration-300"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </figure>
          ))}
        </div>
      )}
    </main>
  );
}
